/**
 * The reporting window for promotion analytics, shared by the per-promotion stats panel and
 * the cross-promotion Overview.
 *
 * SHARED because an operator moves between those two surfaces to answer one question, and
 * meeting two different window controls — or worse, two presets that look the same and resolve
 * differently — makes the figures look inconsistent when they are not.
 *
 * PRESETS, NOT A DATE-RANGE PICKER, and the reasoning came from the panel this was extracted
 * from: AC-14 asks for "basic campaign analytics", and the questions an operator actually asks
 * of a running campaign are relative ("is it working now", "how did last month go"), not
 * arbitrary. A picker would also need validating against each promotion's own start and end,
 * which is a second correctness surface for a screen that reports rather than edits.
 */
export interface PeriodPreset {
  key: string
  label: string
  /** Null is lifetime — the server treats an absent window as cumulative. */
  days: number | null
}

export const PERIOD_PRESETS: PeriodPreset[] = [
  { key: 'all', label: 'All time', days: null },
  { key: 'd30', label: 'Last 30 days', days: 30 },
  { key: 'd90', label: 'Last 90 days', days: 90 },
  { key: 'd365', label: 'Last 12 months', days: 365 }
]

/**
 * Resolves a preset to the half-open [from, to) the server expects, or nulls for lifetime.
 *
 * `to` is NOW rather than end-of-day: a campaign running today should include what happened an
 * hour ago, and rounding up to midnight would report a window that has not finished yet.
 */
export const windowFor = (
  days: number | null
): { from: string | null; to: string | null } => {
  if (days === null) {
    return { from: null, to: null }
  }
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * The window control for the skip-reason view, which is DELIBERATELY NOT the list above.
 *
 * Sharing one control across surfaces exists so an operator answering one question does not
 * meet "two presets that look the same and resolve differently". These two surfaces resolve
 * differently for a reason that no shared control can paper over: the Overview reads
 * cumulative counters that live as long as the promotion, while skip reasons read an
 * evaluation trace swept after a fixed retention. So:
 *
 *   - "All time" is true of the Overview and FALSE here. The honest label is all RETAINED.
 *   - "Last 12 months" is unanswerable here. The server has ~90 days; it clamps and returns
 *     what it has, and the preset goes on promising a year — so an operator reads "no skips
 *     in 12 months" when the fact is "none in the 90 days we keep".
 *
 * Using the shared list would honour the letter of that rule and defeat its purpose.
 */
export const SKIP_PERIOD_PRESETS: PeriodPreset[] = [
  { key: 'all', label: 'All retained', days: null },
  { key: 'd7', label: 'Last 7 days', days: 7 },
  { key: 'd30', label: 'Last 30 days', days: 30 },
  { key: 'd90', label: 'Last 90 days', days: 90 }
]

/**
 * Whether a preset asks for more than the server retains.
 *
 * Not hardcoded against 90: the retention horizon lives in the service's config, has already
 * changed once, and is reported on every response precisely so a client can state the bound
 * rather than assume it. If retention were lowered to 30, the 90-day preset would start
 * over-asking and this begins returning true without anyone editing the portal.
 *
 * `days === null` is never over-asking — it means "whatever is retained", which is exactly
 * what the server will answer.
 */
export const exceedsRetention = (
  days: number | null,
  retentionDays: number | undefined
): boolean =>
  days !== null && typeof retentionDays === 'number' && days > retentionDays
